import React from "react";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from "chart.js";
import { Pie, Bar, Doughnut } from "react-chartjs-2";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
);

// Named export for the Pie chart
export function PieGraph({ data, options }) {
  const defaultOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom" },
      title: { display: false },
    },
  };

  const hasData = data?.datasets?.[0]?.data?.some(val => Number(val) > 0);

  return (
    <div style={{ position: "relative", height: "280px", width: "100%", display: "flex", justifyContent: "center" }}>
      {!hasData && (
        <div style={{
          position: "absolute",
          top: "42%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "180px",
          height: "180px",
          borderRadius: "50%",
          backgroundColor: "#fafafa",
          border: "2px dashed #cfcfcf",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          color: "#9e9e9e",
          fontSize: "14px",
          fontWeight: 500,
          zIndex: 0,
          boxShadow: "inset 0 0 10px rgba(0,0,0,0.02)"
        }}>
          No Data
        </div>
      )}
      <div style={{ position: "relative", zIndex: 1, height: "100%", width: "100%" }}>
        <Pie data={data} options={options || defaultOptions} />
      </div>
    </div>
  );
}

// Named export for the Bar chart
export const BarGraph = ({ data, options, height = '300px' }) => {
  const defaultOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          padding: 16,
          font: {
            size: 12,
            weight: '600',
            family: '"Nunito", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }
        }
      },
      title: {
        display: false,
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false
        }
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: {
          color: 'rgba(0,0,0,0.05)'
        }
      },
    },
    barThickness: 46,
    categoryPercentage: 0.8,
    barPercentage: 0.9,
  };

  return (
    <div style={{ height: height, width: '100%', position: 'relative' }}>
      <Bar data={data} options={options || defaultOptions} />
    </div>
  );
};

// Custom HTML Tooltip to prevent tooltips from being trapped behind absolute HTML text in doughnut centers
const customDoughnutTooltip = (context) => {
  const { chart, tooltip } = context;
  if (!chart?.canvas?.parentNode) return;

  let tooltipEl = chart.canvas.parentNode.querySelector('.chartjs-doughnut-tooltip');

  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.className = 'chartjs-doughnut-tooltip';
    tooltipEl.style.background = 'rgba(15, 23, 42, 0.95)';
    tooltipEl.style.borderRadius = '8px';
    tooltipEl.style.color = '#ffffff';
    tooltipEl.style.opacity = '0';
    tooltipEl.style.pointerEvents = 'none';
    tooltipEl.style.position = 'absolute';
    tooltipEl.style.transition = 'opacity 0.15s ease, transform 0.15s ease';
    tooltipEl.style.padding = '6px 10px';
    tooltipEl.style.fontSize = '0.75rem';
    tooltipEl.style.fontWeight = '600';
    tooltipEl.style.boxShadow = '0 8px 20px -2px rgba(0, 0, 0, 0.35)';
    tooltipEl.style.zIndex = '9999';
    tooltipEl.style.whiteSpace = 'nowrap';
    chart.canvas.parentNode.appendChild(tooltipEl);
  }

  // Hide if no tooltip or empty data
  if (tooltip.opacity === 0 || !tooltip.body || tooltip.body.length === 0) {
    tooltipEl.style.opacity = '0';
    return;
  }

  const titleLines = tooltip.title || [];
  const bodyLines = tooltip.body.map(b => b.lines);
  let innerHtml = '';

  bodyLines.forEach((body, i) => {
    const colors = tooltip.labelColors[i] || {};
    const bg = colors.backgroundColor || '#3b82f6';
    const colorBox = `<span style="display:inline-block;width:8px;height:8px;margin-right:6px;border-radius:2px;background:${bg};vertical-align:middle;"></span>`;
    innerHtml += `<div style="display:flex;align-items:center;">${colorBox}<span>${body}</span></div>`;
  });
  tooltipEl.innerHTML = innerHtml;

  const positionX = chart.canvas.offsetLeft;
  const positionY = chart.canvas.offsetTop;

  if (tooltip.yAlign === 'top') {
    tooltipEl.style.transform = 'translate(-50%, 8px)';
  } else if (tooltip.yAlign === 'bottom') {
    tooltipEl.style.transform = 'translate(-50%, calc(-100% - 8px))';
  } else {
    tooltipEl.style.transform = 'translate(-50%, -50%)';
  }

  tooltipEl.style.opacity = '1';
  tooltipEl.style.left = positionX + tooltip.caretX + 'px';
  tooltipEl.style.top = positionY + tooltip.caretY + 'px';
};

// Named export for the Doughnut chart with center content support
export const DoughnutGraph = ({ data, options, cutout = "76%" }) => {
  const defaultOptions = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 1,
    cutout: cutout,
    layout: {
      padding: 4,
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false, // disable canvas tooltip so custom HTML tooltip floats in front of center text
        external: customDoughnutTooltip,
      },
    },
  };

  return (
    <div style={{ position: "relative", height: "100%", width: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
      <Doughnut data={data} options={options || defaultOptions} />
    </div>
  );
};

// Named export for grouped bar chart (Calibration Trend)
export const GroupedBarGraph = ({ data, options }) => {
  const defaultOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 11, weight: '500' }, color: '#64748b' },
      },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(226, 232, 240, 0.6)' },
        ticks: { font: { size: 11 }, color: '#64748b', maxTicksLimit: 5 },
      },
    },
    barPercentage: 0.8,
    categoryPercentage: 0.6,
  };

  return (
    <div style={{ height: "100%", width: "100%" }}>
      <Bar data={data} options={options || defaultOptions} />
    </div>
  );
};